import React, { useState, useEffect, useMemo } from "react";
import {
  ArrowUp,
  ArrowDown,
  ArrowUpDown,
  ChevronLeft,
  ChevronRight,
  Search,
  Filter,
  Plus,
  Eye,
  Users,
  Building2,
  Mail,
  Phone,
  MapPin,
  UserCheck,
  X,
  RefreshCw,
} from "lucide-react";
import apiService from "../services/api";
import Modal from "../components/ui/Modal";
import AddCustomerModal from "../components/AddCustomerModal";
import toast from "react-hot-toast";

export default function Customers() {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [approving, setApproving] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [rejectionReason, setRejectionReason] = useState("");
  const [salesExecutives, setSalesExecutives] = useState([]);
  const [changingAssignment, setChangingAssignment] = useState(false);
  const [newSalesExecutiveId, setNewSalesExecutiveId] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [salesExecutiveFilter, setSalesExecutiveFilter] = useState("");
  const [districtFilter, setDistrictFilter] = useState("");
  const [showFilters, setShowFilters] = useState(false);

  // Pagination and Sorting State
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [sortConfig, setSortConfig] = useState({
    key: "createdAt",
    direction: "desc",
  });

  const fetchCustomers = async () => {
    try {
      setLoading(true);
      const data = await apiService.getCustomers();
      setCustomers(data);
      setError(null);
    } catch (err) {
      setError("Failed to load customers");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCustomers();
    fetchSalesExecutives();
  }, []);

  const fetchSalesExecutives = async () => {
    try {
      const data = await apiService.getSalesExecutives();
      setSalesExecutives(data);
    } catch (err) {
      console.error("Failed to load sales executives:", err);
    }
  };

  // Filter customers based on search criteria
  const filteredCustomers = customers.filter((customer) => {
    const matchesSearch =
      !searchTerm ||
      customer.businessName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      customer.contactName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      customer.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      customer.contactMobile?.includes(searchTerm);

    const matchesStatus = !statusFilter || customer.status === statusFilter;

    const matchesSalesExecutive =
      !salesExecutiveFilter ||
      customer.assignedSalesExecutive?.id?.toString() === salesExecutiveFilter;

    const matchesDistrict =
      !districtFilter ||
      customer.billDistrict
        ?.toLowerCase()
        .includes(districtFilter.toLowerCase()) ||
      customer.shipDistrict
        ?.toLowerCase()
        .includes(districtFilter.toLowerCase());

    return (
      matchesSearch && matchesStatus && matchesSalesExecutive && matchesDistrict
    );
  });

  // Sorting Logic
  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  const sortedCustomers = useMemo(() => {
    let sortableItems = [...filteredCustomers];
    if (sortConfig.key) {
      sortableItems.sort((a, b) => {
        let aValue = a[sortConfig.key];
        let bValue = b[sortConfig.key];

        if (sortConfig.key.includes(".")) {
          const keys = sortConfig.key.split(".");
          aValue = keys.reduce(
            (obj, key) => (obj && obj[key] !== "undefined" ? obj[key] : ""),
            a
          );
          bValue = keys.reduce(
            (obj, key) => (obj && obj[key] !== "undefined" ? obj[key] : ""),
            b
          );
        }

        if (aValue === null || aValue === undefined) aValue = "";
        if (bValue === null || bValue === undefined) bValue = "";

        if (typeof aValue === "string") {
          aValue = aValue.toLowerCase();
          bValue = bValue.toLowerCase();
        }

        if (aValue < bValue) return sortConfig.direction === "asc" ? -1 : 1;
        if (aValue > bValue) return sortConfig.direction === "asc" ? 1 : -1;
        return 0;
      });
    }
    return sortableItems;
  }, [filteredCustomers, sortConfig]);

  // Pagination Logic
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const paginatedCustomers = sortedCustomers.slice(
    indexOfFirstItem,
    indexOfLastItem
  );
  const totalPages = Math.ceil(sortedCustomers.length / itemsPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter, salesExecutiveFilter, districtFilter]);

  const handleChangeSalesExecutive = async () => {
    if (!newSalesExecutiveId) {
      toast.error("Please select a sales executive");
      return;
    }

    setChangingAssignment(true);
    try {
      await apiService.assignSalesExecutiveToCustomer(
        selectedCustomer.id,
        newSalesExecutiveId
      );
      toast.success("Sales executive assigned successfully!");
      await fetchCustomers();
      setNewSalesExecutiveId("");
      setSelectedCustomer(null);
    } catch (error) {
      toast.error(error.message || "Failed to assign sales executive");
    } finally {
      setChangingAssignment(false);
    }
  };

  const handleApprove = async (customerId) => {
    setApproving(true);
    try {
      const customer = customers.find((c) => c.id === customerId);
      const salesExecutiveId = customer?.salesExecutiveId || undefined;

      await apiService.approveCustomer(customerId, salesExecutiveId);
      toast.success("Customer approved successfully!");
      await fetchCustomers();
      setSelectedCustomer(null);
    } catch (error) {
      toast.error(error.message || "Failed to approve customer");
    } finally {
      setApproving(false);
    }
  };

  const handleReject = async (customerId) => {
    if (!rejectionReason.trim()) {
      toast.error("Please provide a rejection reason");
      return;
    }
    setRejecting(true);
    try {
      await apiService.rejectCustomer(customerId, rejectionReason);
      toast.success("Customer rejected successfully!");
      await fetchCustomers();
      setSelectedCustomer(null);
      setRejectionReason("");
    } catch (error) {
      toast.error(error.message || "Failed to reject customer");
    } finally {
      setRejecting(false);
    }
  };

  const clearFilters = () => {
    setSearchTerm("");
    setStatusFilter("");
    setSalesExecutiveFilter("");
    setDistrictFilter("");
  };

  const hasActiveFilters =
    searchTerm || statusFilter || salesExecutiveFilter || districtFilter;

  const getStatusBadge = (status) => {
    const styles = {
      approved: "bg-emerald-50 text-emerald-700 border border-emerald-200",
      pending: "bg-amber-50 text-amber-700 border border-amber-200",
      rejected: "bg-red-50 text-red-700 border border-red-200",
    };

    return (
      <span
        className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${styles[status]}`}
      >
        <span
          className={`w-1.5 h-1.5 rounded-full mr-1.5 ${
            status === "approved"
              ? "bg-emerald-500"
              : status === "pending"
              ? "bg-amber-500"
              : "bg-red-500"
          }`}
        />
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  // Stats calculation
  const stats = useMemo(
    () => ({
      total: customers.length,
      approved: customers.filter((c) => c.status === "approved").length,
      pending: customers.filter((c) => c.status === "pending").length,
      rejected: customers.filter((c) => c.status === "rejected").length,
    }),
    [customers]
  );

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/4"></div>
          <div className="h-32 bg-gray-200 rounded"></div>
          <div className="h-64 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
          <p className="text-red-600 font-medium">{error}</p>
          <button
            onClick={fetchCustomers}
            className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Customers
          </h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Manage and approve customer accounts
          </p>
        </div>
        <button
          onClick={() => setIsAddModalOpen(true)}
          className="inline-flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 text-white text-sm font-medium rounded-xl hover:from-blue-700 hover:to-indigo-700 shadow-lg shadow-blue-500/25 transition-all duration-200"
        >
          <Plus className="w-4 h-4" />
          Add Customer
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-50 dark:bg-blue-900/30 rounded-lg">
              <Users className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {stats.total}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Total Customers
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-emerald-50 dark:bg-emerald-900/30 rounded-lg">
              <UserCheck className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {stats.approved}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Approved
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-amber-50 dark:bg-amber-900/30 rounded-lg">
              <RefreshCw className="w-5 h-5 text-amber-600 dark:text-amber-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {stats.pending}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Pending
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-red-50 dark:bg-red-900/30 rounded-lg">
              <X className="w-5 h-5 text-red-600 dark:text-red-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {stats.rejected}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Rejected
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex flex-col sm:flex-row gap-4">
            {/* Search */}
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search customers..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
              />
            </div>

            {/* Filter Toggle */}
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`inline-flex items-center gap-2 px-4 py-2.5 border rounded-xl text-sm font-medium transition-all ${
                showFilters || hasActiveFilters
                  ? "bg-blue-50 border-blue-200 text-blue-700 dark:bg-blue-900/30 dark:border-blue-800 dark:text-blue-300"
                  : "bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
              }`}
            >
              <Filter className="w-4 h-4" />
              Filters
              {hasActiveFilters && (
                <span className="ml-1 w-5 h-5 bg-blue-600 text-white text-xs rounded-full flex items-center justify-center">
                  {
                    [statusFilter, salesExecutiveFilter, districtFilter].filter(
                      Boolean
                    ).length
                  }
                </span>
              )}
            </button>
          </div>

          {/* Expanded Filters */}
          {showFilters && (
            <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">
                  Status
                </label>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                >
                  <option value="">All Status</option>
                  <option value="approved">Approved</option>
                  <option value="pending">Pending</option>
                  <option value="rejected">Rejected</option>
                </select>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">
                  Sales Executive
                </label>
                <select
                  value={salesExecutiveFilter}
                  onChange={(e) => setSalesExecutiveFilter(e.target.value)}
                  className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                >
                  <option value="">All Sales Executives</option>
                  {salesExecutives.map((exec) => (
                    <option key={exec.id} value={exec.id}>
                      {exec.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">
                  District
                </label>
                <input
                  type="text"
                  placeholder="Filter by district..."
                  value={districtFilter}
                  onChange={(e) => setDistrictFilter(e.target.value)}
                  className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                />
              </div>

              <div className="flex items-end">
                <button
                  onClick={clearFilters}
                  className="w-full px-4 py-2 text-sm font-medium text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors"
                >
                  Clear Filters
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Results Count */}
        <div className="px-4 py-2 bg-gray-50 dark:bg-gray-900/50 border-b border-gray-200 dark:border-gray-700">
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Showing{" "}
            <span className="font-medium text-gray-700 dark:text-gray-300">
              {indexOfFirstItem + 1}
            </span>{" "}
            to{" "}
            <span className="font-medium text-gray-700 dark:text-gray-300">
              {Math.min(indexOfLastItem, sortedCustomers.length)}
            </span>{" "}
            of{" "}
            <span className="font-medium text-gray-700 dark:text-gray-300">
              {sortedCustomers.length}
            </span>{" "}
            customers
          </p>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 dark:bg-gray-900/50">
                <th
                  className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                  onClick={() => handleSort("businessName")}
                >
                  <div className="flex items-center gap-1.5">
                    Business Name
                    {sortConfig.key === "businessName" ? (
                      sortConfig.direction === "asc" ? (
                        <ArrowUp className="w-3.5 h-3.5" />
                      ) : (
                        <ArrowDown className="w-3.5 h-3.5" />
                      )
                    ) : (
                      <ArrowUpDown className="w-3.5 h-3.5 text-gray-300" />
                    )}
                  </div>
                </th>
                <th
                  className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                  onClick={() => handleSort("contactName")}
                >
                  <div className="flex items-center gap-1.5">
                    Contact Person
                    {sortConfig.key === "contactName" ? (
                      sortConfig.direction === "asc" ? (
                        <ArrowUp className="w-3.5 h-3.5" />
                      ) : (
                        <ArrowDown className="w-3.5 h-3.5" />
                      )
                    ) : (
                      <ArrowUpDown className="w-3.5 h-3.5 text-gray-300" />
                    )}
                  </div>
                </th>
                <th
                  className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors hidden lg:table-cell"
                  onClick={() => handleSort("email")}
                >
                  <div className="flex items-center gap-1.5">
                    Email
                    {sortConfig.key === "email" ? (
                      sortConfig.direction === "asc" ? (
                        <ArrowUp className="w-3.5 h-3.5" />
                      ) : (
                        <ArrowDown className="w-3.5 h-3.5" />
                      )
                    ) : (
                      <ArrowUpDown className="w-3.5 h-3.5 text-gray-300" />
                    )}
                  </div>
                </th>
                <th
                  className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors hidden xl:table-cell"
                  onClick={() => handleSort("assignedSalesExecutive.name")}
                >
                  <div className="flex items-center gap-1.5">
                    Sales Executive
                    {sortConfig.key === "assignedSalesExecutive.name" ? (
                      sortConfig.direction === "asc" ? (
                        <ArrowUp className="w-3.5 h-3.5" />
                      ) : (
                        <ArrowDown className="w-3.5 h-3.5" />
                      )
                    ) : (
                      <ArrowUpDown className="w-3.5 h-3.5 text-gray-300" />
                    )}
                  </div>
                </th>
                <th
                  className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors hidden md:table-cell"
                  onClick={() => handleSort("status")}
                >
                  <div className="flex items-center gap-1.5">
                    Status
                    {sortConfig.key === "status" ? (
                      sortConfig.direction === "asc" ? (
                        <ArrowUp className="w-3.5 h-3.5" />
                      ) : (
                        <ArrowDown className="w-3.5 h-3.5" />
                      )
                    ) : (
                      <ArrowUpDown className="w-3.5 h-3.5 text-gray-300" />
                    )}
                  </div>
                </th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
              {paginatedCustomers.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-4 py-12 text-center">
                    <Users className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
                    <p className="text-gray-500 dark:text-gray-400 font-medium">
                      No customers found
                    </p>
                    <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">
                      Try adjusting your search or filters
                    </p>
                  </td>
                </tr>
              ) : (
                paginatedCustomers.map((customer) => (
                  <tr
                    key={customer.id}
                    className="hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white font-semibold text-sm flex-shrink-0">
                          {customer.businessName?.charAt(0)?.toUpperCase() ||
                            "C"}
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                            {customer.businessName || "-"}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1">
                            <Phone className="w-3 h-3" />
                            {customer.contactMobile || "-"}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-sm text-gray-900 dark:text-white">
                        {customer.contactName || "-"}
                      </p>
                    </td>
                    <td className="px-4 py-3 hidden lg:table-cell">
                      <p className="text-sm text-gray-600 dark:text-gray-400 truncate max-w-[200px]">
                        {customer.email}
                      </p>
                    </td>
                    <td className="px-4 py-3 hidden xl:table-cell">
                      <div>
                        <p className="text-sm text-gray-900 dark:text-white">
                          {customer.assignedSalesExecutive?.name ||
                            "Not Assigned"}
                        </p>
                        {customer.assignedSalesExecutive?.employeeId && (
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            ID: {customer.assignedSalesExecutive.employeeId}
                          </p>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 hidden md:table-cell">
                      {getStatusBadge(customer.status)}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={() => setSelectedCustomer(customer)}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded-lg transition-colors"
                      >
                        <Eye className="w-4 h-4" />
                        <span className="hidden sm:inline">View</span>
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 dark:border-gray-700">
            <p className="text-sm text-gray-500 dark:text-gray-400 hidden sm:block">
              Page {currentPage} of {totalPages}
            </p>
            <div className="flex items-center gap-1">
              <button
                onClick={() => paginate(currentPage - 1)}
                disabled={currentPage === 1}
                className={`p-2 rounded-lg border transition-colors ${
                  currentPage === 1
                    ? "text-gray-300 dark:text-gray-600 border-gray-200 dark:border-gray-700 cursor-not-allowed"
                    : "text-gray-600 dark:text-gray-400 border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800"
                }`}
              >
                <ChevronLeft className="w-4 h-4" />
              </button>

              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter(
                  (num) =>
                    num === 1 ||
                    num === totalPages ||
                    (num >= currentPage - 1 && num <= currentPage + 1)
                )
                .map((number, index, array) => (
                  <React.Fragment key={number}>
                    {index > 0 && array[index - 1] !== number - 1 && (
                      <span className="px-2 text-gray-400">...</span>
                    )}
                    <button
                      onClick={() => paginate(number)}
                      className={`min-w-[36px] h-9 rounded-lg border text-sm font-medium transition-colors ${
                        currentPage === number
                          ? "bg-blue-600 text-white border-blue-600"
                          : "text-gray-600 dark:text-gray-400 border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800"
                      }`}
                    >
                      {number}
                    </button>
                  </React.Fragment>
                ))}

              <button
                onClick={() => paginate(currentPage + 1)}
                disabled={currentPage === totalPages}
                className={`p-2 rounded-lg border transition-colors ${
                  currentPage === totalPages
                    ? "text-gray-300 dark:text-gray-600 border-gray-200 dark:border-gray-700 cursor-not-allowed"
                    : "text-gray-600 dark:text-gray-400 border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800"
                }`}
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Customer Details Modal */}
      <Modal
        isOpen={!!selectedCustomer}
        onClose={() => setSelectedCustomer(null)}
        title="Customer Details"
        size="lg"
      >
        {selectedCustomer && (
          <div className="max-h-[70vh] overflow-y-auto space-y-6">
            {/* Customer Header */}
            <div className="flex items-start gap-4 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-xl">
              <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white font-bold text-2xl">
                {selectedCustomer.businessName?.charAt(0)?.toUpperCase() || "C"}
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  {selectedCustomer.businessName || "N/A"}
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 flex items-center gap-2 mt-1">
                  <Mail className="w-4 h-4" />
                  {selectedCustomer.email}
                </p>
                <div className="mt-2">
                  {getStatusBadge(selectedCustomer.status)}
                </div>
              </div>
            </div>

            {/* Basic Info */}
            <div className="space-y-4">
              <h4 className="text-sm font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                <Building2 className="w-4 h-4 text-blue-600" />
                Business Details
              </h4>
              <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 dark:bg-gray-800/50 rounded-xl">
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Category
                  </p>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                    {selectedCustomer.businessCategory || "-"}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    GST Number
                  </p>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                    {selectedCustomer.gstNumber || "-"}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    PAN Number
                  </p>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                    {selectedCustomer.panNumber || "-"}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Years in Business
                  </p>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                    {selectedCustomer.yearsInBusiness || "-"}
                  </p>
                </div>
              </div>
            </div>

            {/* Contact Info */}
            <div className="space-y-4">
              <h4 className="text-sm font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                <Phone className="w-4 h-4 text-blue-600" />
                Contact Person
              </h4>
              <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 dark:bg-gray-800/50 rounded-xl">
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Name
                  </p>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                    {selectedCustomer.contactName || "-"}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Mobile
                  </p>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                    {selectedCustomer.contactMobile || "-"}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Email
                  </p>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                    {selectedCustomer.contactEmail || "-"}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Role
                  </p>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                    {selectedCustomer.contactRole || "-"}
                  </p>
                </div>
              </div>
            </div>

            {/* Sales Executive Assignment */}
            <div className="space-y-4">
              <h4 className="text-sm font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                <UserCheck className="w-4 h-4 text-blue-600" />
                Sales Executive
              </h4>
              <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-xl border border-blue-100 dark:border-blue-800">
                {selectedCustomer.assignedSalesExecutive ? (
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-blue-600 flex items-center justify-center text-white font-semibold">
                      {selectedCustomer.assignedSalesExecutive.name
                        ?.charAt(0)
                        ?.toUpperCase()}
                    </div>
                    <div>
                      <p className="font-medium text-gray-900 dark:text-white">
                        {selectedCustomer.assignedSalesExecutive.name}
                      </p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        {selectedCustomer.assignedSalesExecutive.email}
                      </p>
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-amber-600 dark:text-amber-400">
                    No sales executive assigned
                  </p>
                )}

                <div className="mt-4 pt-4 border-t border-blue-100 dark:border-blue-800">
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Change Assignment
                  </label>
                  <div className="flex gap-2">
                    <select
                      value={newSalesExecutiveId}
                      onChange={(e) => setNewSalesExecutiveId(e.target.value)}
                      className="flex-1 px-3 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                    >
                      <option value="">Select Sales Executive</option>
                      {salesExecutives.map((se) => (
                        <option key={se.id} value={se.id}>
                          {se.name} ({se.employeeId || "N/A"})
                        </option>
                      ))}
                    </select>
                    <button
                      onClick={handleChangeSalesExecutive}
                      disabled={!newSalesExecutiveId || changingAssignment}
                      className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      {changingAssignment ? "Saving..." : "Assign"}
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Addresses */}
            <div className="space-y-4">
              <h4 className="text-sm font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                <MapPin className="w-4 h-4 text-blue-600" />
                Addresses
              </h4>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="p-4 bg-gray-50 dark:bg-gray-800/50 rounded-xl">
                  <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase mb-2">
                    Billing Address
                  </p>
                  <p className="text-sm text-gray-900 dark:text-white">
                    {selectedCustomer.billAddressLine || "-"}
                  </p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {[
                      selectedCustomer.billDistrict,
                      selectedCustomer.billState,
                      selectedCustomer.billPinCode,
                    ]
                      .filter(Boolean)
                      .join(", ")}
                  </p>
                </div>
                <div className="p-4 bg-gray-50 dark:bg-gray-800/50 rounded-xl">
                  <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase mb-2">
                    Shipping Address
                  </p>
                  <p className="text-sm text-gray-900 dark:text-white">
                    {selectedCustomer.shipAddressLine || "-"}
                  </p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {[
                      selectedCustomer.shipDistrict,
                      selectedCustomer.shipState,
                      selectedCustomer.shipPinCode,
                    ]
                      .filter(Boolean)
                      .join(", ")}
                  </p>
                </div>
              </div>
            </div>

            {/* B2B Features */}
            <div className="space-y-4">
              <h4 className="text-sm font-semibold text-gray-900 dark:text-white">
                B2B Features
              </h4>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 p-4 bg-gray-50 dark:bg-gray-800/50 rounded-xl">
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Credit Limit
                  </p>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                    ₹{selectedCustomer.creditLimit?.toLocaleString() || "0"}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Payment Terms
                  </p>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                    {selectedCustomer.paymentTerms || "-"}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Customer Tier
                  </p>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                    {selectedCustomer.customerTier || "-"}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Discount %
                  </p>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                    {selectedCustomer.discountPercentage || "0"}%
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Min Order Value
                  </p>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                    ₹
                    {selectedCustomer.minimumOrderValue?.toLocaleString() ||
                      "0"}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Lead Time
                  </p>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                    {selectedCustomer.leadTime || "-"} days
                  </p>
                </div>
              </div>
            </div>

            {/* Action Buttons for Pending */}
            {selectedCustomer.status === "pending" && (
              <div className="flex gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
                <button
                  onClick={() => handleApprove(selectedCustomer.id)}
                  disabled={approving}
                  className="flex-1 px-4 py-2.5 bg-emerald-600 text-white text-sm font-medium rounded-xl hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {approving ? "Approving..." : "✓ Approve Customer"}
                </button>
                <button
                  onClick={() => {
                    const reason = prompt(
                      "Please provide a reason for rejection:"
                    );
                    if (reason) {
                      setRejectionReason(reason);
                      handleReject(selectedCustomer.id);
                    }
                  }}
                  disabled={rejecting}
                  className="flex-1 px-4 py-2.5 bg-red-600 text-white text-sm font-medium rounded-xl hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {rejecting ? "Rejecting..." : "✕ Reject Customer"}
                </button>
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* Add Customer Modal */}
      <AddCustomerModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onSuccess={fetchCustomers}
      />
    </div>
  );
}
